<template>
	<v-card>
		<div id="profileView" tile class="pa-0 chart-board" @contextmenu="show" ref="profile_view"></div>
		<!-- disabled JsonViewer -->
		<!-- <JsonViewer v-if="tooltipData" :data="tooltipData" :style="tooltipStyle" /> -->

		<v-menu v-model="showMenu" :target="[x, y]" absolute>
			<v-list density="compact">
				<v-list-item class="list-ChartContextMenu" v-for="(item, index) in contextMenuItemsToShow" :key="index"
					@click="onContextMenuClick($event, item)" :disabled="item.disabled">
					<v-list-item-title>
						{{ $t(item.title) }}
					</v-list-item-title>
				</v-list-item>
			</v-list>
		</v-menu>
	</v-card>
</template>

<script lang="ts">
import { useStore } from "vuex";
import "./style.css";
import { ObjectType } from "@/models/objects/types";
import JsonViewer from "@/components/JsonViewer.vue";
import { register, unregister } from "@/utils/chartViewUpdaters";

enum CONTEXTMENUITEM {
	ADD_SLOPE_BREAK = "add-slope-break",
	REMOVE_SLOPE_BREAK = "remove-slope-break",
	UPDATE_SLOPE_BREAK_BY_COORDINATE_VALUE = "update_slope_break_by_coordinate_value",
	UNDO = "undo",
	REDO = "redo",
	END_EDITING = "end_editing",
	ENABLE_SLOPE_BREAK = "enable_slope_break",
	DISABLE_SLOPE_BREAK = "disable_slope_break",
}

type ContextMenuItem = {
	menuItem: CONTEXTMENUITEM;
	title: string;
	onClick: () => void;
	disabled: boolean;
};

export default {
	name: "ProfileViewDialog",
	components: {
		JsonViewer,
	},
	props: {
		id: String,
	},
	setup() {
		const store = useStore();
		return { store };
	},
	computed: {
		panelName(): string {
			return this.store.state.panelName;
		},
		contextMenuItemsToShow(): ContextMenuItem[] {
			//if the slopebreak is first or last, it is always enabled and cannot be deleted
			//if the slopebreak is associated with control point, it cannot be deleted
			//if the slopebreak is not associated with control point, it is always enabled and can be deleted
			//if the slopebreak is disabled, it cannot be deleted, updated

			const items = [
				CONTEXTMENUITEM.ADD_SLOPE_BREAK,
				CONTEXTMENUITEM.REMOVE_SLOPE_BREAK,
				CONTEXTMENUITEM.UPDATE_SLOPE_BREAK_BY_COORDINATE_VALUE,
				CONTEXTMENUITEM.UNDO,
				CONTEXTMENUITEM.REDO,
				CONTEXTMENUITEM.END_EDITING,
				CONTEXTMENUITEM.ENABLE_SLOPE_BREAK,
				CONTEXTMENUITEM.DISABLE_SLOPE_BREAK,
			];

			const itemsToEnable = [...items];
			const itemsToDisable = [];
			const itemsToRemove = [];

			// no data
			if (
				this.pathInfo.pathId === undefined &&
				this.pathInfo.pointId === undefined
			) {
				itemsToRemove.push(...items);
			}

			if (this.pathInfo.pointId !== undefined) {
				itemsToRemove.push(CONTEXTMENUITEM.ADD_SLOPE_BREAK);
				// first or last
				if (
					+this.pathInfo.pointId === 0 ||
					+this.pathInfo.pointId === this.slopeBreaks.length - 1
				) {
					itemsToDisable.push(
						CONTEXTMENUITEM.REMOVE_SLOPE_BREAK,
						CONTEXTMENUITEM.DISABLE_SLOPE_BREAK,
					);
				}
				// associated with control point
				if (
					this.slopeBreaks[this.pathInfo.pointId].associatedControlPointId !=
					undefined
				) {
					itemsToDisable.push(CONTEXTMENUITEM.REMOVE_SLOPE_BREAK);
				} else {
					itemsToDisable.push(CONTEXTMENUITEM.DISABLE_SLOPE_BREAK);
				}

				// disabled
				if (this.slopeBreaks[+this.pathInfo.pointId].isActive === false) {
					itemsToRemove.push(CONTEXTMENUITEM.DISABLE_SLOPE_BREAK);
					itemsToDisable.push(
						CONTEXTMENUITEM.REMOVE_SLOPE_BREAK,
						CONTEXTMENUITEM.UPDATE_SLOPE_BREAK_BY_COORDINATE_VALUE,
					);
				}

				// enabled
				if (this.slopeBreaks[+this.pathInfo.pointId].isActive === true) {
					itemsToRemove.push(CONTEXTMENUITEM.ENABLE_SLOPE_BREAK);
				}
			} else {
				itemsToRemove.push(
					CONTEXTMENUITEM.REMOVE_SLOPE_BREAK,
					CONTEXTMENUITEM.UPDATE_SLOPE_BREAK_BY_COORDINATE_VALUE,
					CONTEXTMENUITEM.ENABLE_SLOPE_BREAK,
					CONTEXTMENUITEM.DISABLE_SLOPE_BREAK,
				);
			}

			return this.contextMenuItems
				.filter((item) => !itemsToRemove.includes(item.menuItem))
				.map((item) =>
					itemsToEnable.includes(item.menuItem)
						? { ...item, disabled: false }
						: { ...item },
				)
				.map((item) =>
					itemsToDisable.includes(item.menuItem)
						? { ...item, disabled: true }
						: { ...item },
				);
		},
	},
	watch: {
		panelName() {
			this.showMenu = false;
		},
	},
	data: function () {
		return {
			showMenu: false,
			x: 0,
			y: 0,
			offsetX: 0,
			offsetY: 0,
			pathInfo: { pathId: undefined, pointId: undefined, data: undefined },
			slopeBreaks: [],
			updateBound: null,
			tooltipData: null,
			tooltipStyle: {
				display: "none",
				top: "0px",
				left: "0px",
				position: "absolute",
				backgroundColor: "#444444e6",
				border: "1px solid black",
				padding: "5px",
				zIndex: "9999",
				pointerEvents: "auto",
				transform: "translateY(-100%)",
			},
			contextMenuItems: [
				{
					menuItem: CONTEXTMENUITEM.ADD_SLOPE_BREAK,
					title: "ADD",
					onClick: () => {
						this.addSlopeBreak();
					},
					disabled: true,
				},
				{
					menuItem: CONTEXTMENUITEM.REMOVE_SLOPE_BREAK,
					title: "Remove",
					onClick: () => {
						this.removeSlopeBreak();
					},
					disabled: true,
				},
				{
					menuItem: CONTEXTMENUITEM.UPDATE_SLOPE_BREAK_BY_COORDINATE_VALUE,
					title: "Update",
					onClick: () => {
						this.updateSlopeBreakByCoordinateValue();
					},
					disabled: true,
				},
				{
					menuItem: CONTEXTMENUITEM.UNDO,
					title: "Undo",
					onClick: () => {
						this.undo();
					},
					disabled: true,
				},
				{
					menuItem: CONTEXTMENUITEM.REDO,
					title: "Redo",
					onClick: () => {
						this.redo();
					},
					disabled: true,
				},
				{
					menuItem: CONTEXTMENUITEM.ENABLE_SLOPE_BREAK,
					title: "Enable",
					onClick: () => {
						this.enableSlopeBreak();
					},
					disabled: true,
				},
				{
					menuItem: CONTEXTMENUITEM.DISABLE_SLOPE_BREAK,
					title: "Disable",
					onClick: () => {
						this.disableSlopeBreak();
					},
					disabled: true,
				},
			],
		};
	},
	mounted() {
		this.updateBound = this.update.bind(this);
		register(this.updateBound);

		this.$nextTick(() => {
			this.update();
		});
		if (this.store.getters.viewer) {
			this.store.getters.viewer.setAltitudeTooltipCallback(
				this.handleTooltipUpdate.bind(this),
			);
		}
	},
	beforeUnmount() {
		if (this.store.getters.viewer) {
			this.store.getters.viewer.setAltitudeTooltipCallback(undefined);
		}
		unregister(this.updateBound);
	},
	methods: {
		handleTooltipUpdate(data, position, visible) {
			if (visible && data) {
				this.tooltipData = data;
				const left = position.x + 10;
				const top = position.y;
				this.tooltipStyle = {
					...this.tooltipStyle,
					display: "block",
					left: `${left}px`,
					top: `${top}px`,
				};
			} else {
				this.tooltipStyle.display = "none";
			}
		},
		show(e) {
			this.store.dispatch("changePanel", "ProfileView");
			e.preventDefault();
			this.showMenu = false;
			this.x = e.clientX + 10;
			this.y = e.clientY;
			this.offsetX = e.offsetX;
			this.offsetY = e.offsetY;
			this.$nextTick(() => {
				if (this.store.state.object.objectUnderEdit?.type === ObjectType.ROAD) {
					const pickResult = window["viewer"].pickPathandPoint(
						e.pageX,
						e.pageY,
						"altitudeView",
					);

					if (pickResult !== undefined) {
						this.pathInfo.pathId = pickResult.pathId;
						this.pathInfo.data = pickResult.data;
						const slopeBreaks = window["viewer"].getSlopeBreaks(
							pickResult.pathId,
						);
						this.pathInfo.pointId =
							pickResult.pointIndex === "entityPath"
								? undefined
								: pickResult.pointIndex;
						this.slopeBreaks = [
							...(typeof slopeBreaks === "object" ? slopeBreaks : []),
						];
					} else {
						this.pathInfo.pathId = undefined;
						this.pathInfo.pointId = undefined;
						this.pathInfo.data = undefined;
					}
					this.showMenu = true;
				}
			});
		},
		async onContextMenuClick(e, item) {
			e.preventDefault();
			await item.onClick();
		},
		onAddContextMenuClick(e, id) {
			e.preventDefault();
			switch (id) {
				case 1:
					window["viewer"].updateSlopeBreaksFromLongitudinalView(
						this.pathInfo.pathId,
						this.pathInfo.data,
					);
					break;
			}
		},
		update() {
			const canvas =
				window["viewer"] && window["viewer"].getCanvasForView("altitudeView");
			const element = this.$refs.profile_view;
			if (canvas && element instanceof HTMLElement) {
				for (const node of Array.from(element.children)) {
					element.removeChild(node);
				}
				element.appendChild(canvas);
				canvas.height = element.getBoundingClientRect().height;
				canvas.width = element.getBoundingClientRect().width;
				window["viewer"].resize2dView(
					{ width: canvas.width, height: canvas.height },
					"altitudeView",
				);
			}
		},
		addSlopeBreak() {
			if (!this.pathInfo.data) {
				console.warn("Cannot add slope break: no path data available");
				return;
			}
			const slopeBreak = {
				percentage: this.pathInfo.data.length,
				height: this.pathInfo.data.height,
				isActive: true,
				associatedControlPointId: undefined,
			};
			this.slopeBreaks.splice(this.pathInfo.data.index, 0, slopeBreak);
			this.slopeBreaks.sort((a, b) => a.percentage - b.percentage);
			this.store.getters.viewer.updateSlopeBreaksFromLongitudinalView(
				this.pathInfo.pathId,
				this.slopeBreaks,
			);
		},
		removeSlopeBreak() {
			this.store.getters.viewer.removeSelectedSlopeBreak(this.pathInfo.pathId);
		},
		updateSlopeBreakByCoordinateValue() {
			window.dispatchEvent(
				new CustomEvent("openPropertyDialog", {
					detail: {
						id: this.pathInfo.pathId,
						type: 3,
						pointId: this.pathInfo.pointId,
						entityUnderEdit: ObjectType.ROAD,
						disabilityConditions: "XY",
						origin: "AltitudeView",
					},
				}),
			);
		},
		undo() {
			this.store.getters.viewer.undo();
		},
		redo() {
			this.store.getters.viewer.redo();
		},
		enableSlopeBreak() {
			if (
				this.pathInfo.pointId === undefined ||
				!this.slopeBreaks[this.pathInfo.pointId]
			) {
				console.warn("Cannot enable slope break: no path data available");
				return;
			}
			this.slopeBreaks[this.pathInfo.pointId].isActive = true;
			this.store.getters.viewer.updateSlopeBreaksFromLongitudinalView(
				this.pathInfo.pathId,
				this.slopeBreaks,
			);
		},
		disableSlopeBreak() {
			if (
				this.pathInfo.pointId === undefined ||
				!this.slopeBreaks[this.pathInfo.pointId]
			) {
				console.warn("Cannot disable slope break: no path data available");
				return;
			}
			this.slopeBreaks[this.pathInfo.pointId].isActive = false;
			this.store.getters.viewer.updateSlopeBreaksFromLongitudinalView(
				this.pathInfo.pathId,
				this.slopeBreaks,
			);
		},
	},
};
</script>

<style scoped>
.v-card {
	display: flex;
	flex-direction: column;
}

.chart-board {
	flex: 1;
	min-height: 0;
}
</style>
